import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { cognitoAuthService, SignUpData } from '../services/cognitoAuthService.ts';
import { User, AuthState } from '../types/auth.ts';

interface CognitoUserAttributes {
  email?: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  email_verified?: string;
  [key: string]: string | undefined;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpData) => Promise<void>;
  confirmSignUp: (email: string, code: string, password?: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  forgotPassword: (data: { email: string }) => Promise<void>;
  resetPassword: (data: { email: string; code: string; newPassword: string }) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  updateUser: (updatedUser: Partial<User>) => void;
  refreshUserProfile: () => Promise<void>;

  redirectToPartnerSSO: () => Promise<void>;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to decode JWT and extract user info
  const parseJWTToken = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  // NEW: Map Cognito token to a simple provider label we store in Supabase
  const getProviderFromToken = (tokenData: any): 'password' | 'google' | 'microsoft' => {
    const identities = tokenData?.identities;
    if (Array.isArray(identities) && identities.length > 0) {
      const p = (identities[0]?.providerName || identities[0]?.providerType || '').toLowerCase();
      if (p.includes('google')) return 'google';
      if (p.includes('microsoft') || p.includes('azure')) return 'microsoft';
      // Fall through to password if we can't confidently map it
    }
    return 'password';
  };

  const setUserFromTokens = useCallback((tokens: { accessToken?: string; idToken?: string; refreshToken?: string }) => {
    console.log('🎯 setUserFromTokens called with tokens:', {
      hasAccessToken: !!tokens.accessToken,
      hasIdToken: !!tokens.idToken,
      hasRefreshToken: !!tokens.refreshToken
    });

    if (tokens.idToken) {
      console.log('🔍 Parsing ID token...');
      const tokenData = parseJWTToken(tokens.idToken);
      console.log('🔍 Token data parsed:', !!tokenData);
      if (tokenData) {
        // Create full name from first and last name
        const firstName = tokenData.given_name || '';
        const lastName = tokenData.family_name || '';
        const fullName = `${firstName} ${lastName}`.trim() || tokenData.email?.split('@')[0] || '';

        // Check if this is an OAuth user (has identities claim)
        const isOAuthUser = tokenData.identities && tokenData.identities.length > 0;

        const userData: User = {
          id: tokenData.sub || '',
          email: tokenData.email || '',
          name: fullName,
          emailVerified: isOAuthUser || tokenData.email_verified === true, // OAuth users are verified
          mfaEnabled: false,
          createdAt: new Date().toISOString(),
          lastSignIn: new Date().toISOString(),
        };

        console.log('🔍 Setting user with full name:', fullName);
        console.log('🔍 OAuth user detected:', isOAuthUser);
        setUser(userData);

        // Store tokens
        localStorage.setItem('accessToken', tokens.accessToken || '');
        localStorage.setItem('idToken', tokens.idToken || '');
        localStorage.setItem('refreshToken', tokens.refreshToken || '');

        // NEW: Fire-and-forget login audit (password or social)
        const provider = getProviderFromToken(tokenData);
        import('../lib/auditClient.ts')
          .then(({ auditLogin }) => auditLogin(tokens.idToken!, provider))
          .catch((e) => console.warn('audit login failed', e));

        // Set loading to false after successfully setting user
        setIsLoading(false);
      } else {
        console.error('❌ Failed to parse ID token');
        setIsLoading(false);
      }
    } else {
      console.error('❌ No ID token provided');
      setIsLoading(false);
    }
  }, []);

  // OAuth callback handler - added without affecting existing code
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (code) {
        try {
          setIsLoading(true);
          setError(null); // Clear any previous errors
          console.log('🔍 OAuth callback received, code:', code);

          // Exchange code for tokens using your cognitoAuthService
          const tokens = await cognitoAuthService.exchangeCodeForTokens(code);
          console.log('🔍 Tokens received:', tokens);

          // Set user from tokens (your existing logic)
          setUserFromTokens(tokens);

          // Clear error on success
          setError(null);

          // Clean up URL to remove the code parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('OAuth callback error:', error);
          setError('OAuth sign-in failed. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleOAuthCallback();
  }, [setUserFromTokens]); // Include setUserFromTokens dependency

  // Initialize authentication state from localStorage on app start
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 AuthContext: Initializing authentication state...');
      try {
        const accessToken = localStorage.getItem('accessToken');
        const idToken = localStorage.getItem('idToken');
        const refreshToken = localStorage.getItem('refreshToken');

        console.log('🔍 Stored tokens check:', {
          hasAccessToken: !!accessToken,
          hasIdToken: !!idToken,
          hasRefreshToken: !!refreshToken,
          accessTokenLength: accessToken?.length || 0,
          idTokenLength: idToken?.length || 0
        });

        if (accessToken && idToken) {
          console.log('🔄 Restoring authentication state from localStorage');
          setIsLoading(true);

          // Restore user from stored tokens
          const tokens = {
            accessToken,
            idToken,
            refreshToken: refreshToken || ''
          };

          console.log('📝 Calling setUserFromTokens with restored tokens');
          // Use the same logic as sign-in to set user from tokens
          setUserFromTokens(tokens);
          console.log('✅ Authentication state restored successfully');
        } else {
          console.log('🔍 No stored tokens found, user not authenticated');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('❌ Error restoring authentication state:', error);
        // Clear invalid tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('refreshToken');
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [setUserFromTokens]); // Include setUserFromTokens dependency



  // Update user profile data
  const updateUser = (updatedUser: Partial<User>): void => {
    if (user) {
      const newUser = { ...user, ...updatedUser };
      console.log('🔍 Updating user with:', newUser); // Debug log
      setUser(newUser);
    }
  };

  // Refresh user profile from Cognito
  const refreshUserProfile = async (): Promise<void> => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !user) return;

    try {
      const userData = await cognitoAuthService.getCurrentUser(accessToken);

      // Type assertion for attributes
      const attributes = userData.attributes as CognitoUserAttributes;

      // Create full name from first and last name
      const firstName = attributes?.given_name || '';
      const lastName = attributes?.family_name || '';
      const fullName = `${firstName} ${lastName}`.trim() || user.name;

      const updatedUser: User = {
        ...user,
        email: attributes?.email || user.email,
        name: fullName, // Now uses full name
        emailVerified: attributes?.email_verified === 'true',
      };

      console.log('🔍 Refreshing user profile with full name:', fullName); // Debug log
      setUser(updatedUser);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  };

  // Handle page visibility changes (user returning from external sites)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        // Verify tokens are still valid by attempting to refresh user profile
        refreshUserProfile().catch((error) => {
          console.error('Failed to refresh user profile on visibility change:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const signUp = async (data: SignUpData): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await cognitoAuthService.signUp(data);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSignUp = async (email: string, code: string, password?: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await cognitoAuthService.confirmSignUp(email, code);

      // After successful confirmation, automatically sign the user in if we have their password
      if (password) {
        const tokens = await cognitoAuthService.signIn(email, password);
        setUserFromTokens(tokens); // will also trigger login audit
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Confirmation failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resendConfirmationCode = async (email: string): Promise<void> => {
    try {
      setError(null);
      await cognitoAuthService.resendConfirmationCode(email);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Resend failed';
      setError(errorMessage);
      throw error;
    }
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const tokens = await cognitoAuthService.signIn(email, password);
      setUserFromTokens(tokens); // will also trigger login audit
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setUser(null);
    setIsLoading(false); // Reset loading state
    setError(null); // Clear any errors
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
  };

  const forgotPassword = async (data: { email: string }): Promise<void> => {
    try {
      setError(null);
      await cognitoAuthService.forgotPassword(data.email);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset request failed';
      setError(errorMessage);
      throw error;
    }
  };

  const resetPassword = async (data: { email: string; code: string; newPassword: string }): Promise<void> => {
    try {
      setError(null);
      await cognitoAuthService.confirmForgotPassword(data.email, data.code, data.newPassword);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      setError(errorMessage);
      throw error;
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  // Add the redirectToPartnerSSO function that UserMenu expects
  const redirectToPartnerSSO = async (): Promise<void> => {
    // Implement your partner SSO logic here
    console.log('Redirecting to partner SSO...');
    // For now, just a placeholder
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    signIn,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    resetPassword,
    signOut,
    clearError,
    updateUser,
    refreshUserProfile,

    redirectToPartnerSSO,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
